import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { destinations } from '../data/mock';

const DestinationsPreview = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const itemsPerSlide = 3;
  const totalSlides = Math.ceil(destinations.length / itemsPerSlide);

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 5000);
    return () => clearInterval(interval);
  }, [totalSlides]);

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  };

  const getCurrentDestinations = () => {
    const startIndex = currentSlide * itemsPerSlide;
    return destinations.slice(startIndex, startIndex + itemsPerSlide);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Explore Our
            <span className="block bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] bg-clip-text text-transparent">
              Featured Destinations
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From ancient temples to pristine beaches, discover the world's most captivating destinations 
            through our expertly crafted travel experiences.
          </p>
        </div>

        {/* Destinations Carousel */}
        <div className="relative mb-12">
          {/* Carousel Container */}
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div key={slideIndex} className="w-full flex-shrink-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-4">
                    {destinations
                      .slice(slideIndex * itemsPerSlide, (slideIndex + 1) * itemsPerSlide)
                      .map((destination, index) => (
                        <Card 
                          key={destination.id} 
                          className="group cursor-pointer overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-white"
                        >
                          <div className="relative overflow-hidden">
                            <div
                              className="h-64 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                              style={{ backgroundImage: `url(${destination.heroImage})` }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                            
                            {/* Destination badge */}
                            <div className="absolute top-4 left-4">
                              <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center">
                                <MapPin size={14} className="text-[#6C30ED] mr-1" />
                                <span className="text-sm font-semibold text-gray-900">{destination.name}</span>
                              </div>
                            </div>

                            {/* Overlay content */}
                            <div className="absolute bottom-0 left-0 right-0 p-6">
                              <h3 className="text-2xl font-bold text-white mb-2">
                                {destination.name}
                              </h3>
                              <p className="text-white/90 mb-4">
                                {destination.shortDescription}
                              </p>
                              
                              {/* Highlights */}
                              <div className="flex flex-wrap gap-2 mb-4">
                                {destination.highlights.slice(0, 2).map((highlight, idx) => (
                                  <span
                                    key={idx}
                                    className="bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full"
                                  >
                                    {highlight}
                                  </span>
                                ))}
                                {destination.highlights.length > 2 && (
                                  <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                                    +{destination.highlights.length - 2} more
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <CardContent className="p-6">
                            {/* Experiences */}
                            <div className="mb-4">
                              <h4 className="font-semibold text-gray-900 mb-3">Popular Experiences</h4>
                              <div className="flex flex-wrap gap-2">
                                {destination.experiences.slice(0, 3).map((experience, idx) => (
                                  <span
                                    key={idx}
                                    className="bg-gradient-to-r from-[#6C30ED]/10 to-[#4E8EFF]/10 text-[#6C30ED] text-sm px-3 py-1 rounded-full border border-[#6C30ED]/20"
                                  >
                                    {experience}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* CTA */}
                            <Link to={`/destinations/${destination.id}`}>
                              <Button 
                                variant="ghost" 
                                className="w-full text-[#6C30ED] hover:bg-[#6C30ED]/10 font-semibold group/btn"
                              >
                                Explore {destination.name}
                                <ArrowRight size={16} className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
                              </Button>
                            </Link>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 text-gray-700 hover:text-[#6C30ED] transition-all duration-200 z-10"
            disabled={currentSlide === 0}
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 text-gray-700 hover:text-[#6C30ED] transition-all duration-200 z-10"
            disabled={currentSlide === totalSlides - 1}
          >
            <ChevronRight size={24} />
          </button>

          {/* Carousel Indicators */}
          <div className="flex justify-center mt-8 space-x-3">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentSlide 
                    ? 'bg-[#6C30ED] scale-125' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Slide counter */}
          <div className="text-center mt-4">
            <span className="text-sm text-gray-500">
              {currentSlide + 1} of {totalSlides}
            </span>
          </div>
        </div>

        {/* View all destinations CTA */}
        <div className="text-center">
          <Link to="/destinations">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] hover:from-[#5c28d4] hover:to-[#3d7eff] text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              View All Destinations
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DestinationsPreview;